import { c as _c } from "react/compiler-runtime";
import { dehydrate, Hydrate, QueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { LegContentRecord } from '~/libs/dato/fragments/legContentFragment';
import type { HeroRouteRecord } from '~/server/trip/getHeroRoutesForTrip';
import type { PlaceImageRecord } from '~/server/trip/getPlaceImagesForTrip';
import type { PlaceNames } from '~/server/trip/getPlaceNamesForTrip';
import type { TripOverview } from '~/services/engine-ts';
type Props = {
  tripId: string;
  locale: string;
  overviewData: TripOverview;
  placeNames: PlaceNames;
  children: ReactNode;
};
const makeOverviewQueryKey = (tripId: string) => [['trip', 'getOverview'], {
  input: {
    tripId
  },
  type: 'query'
}] as const;
export const makeHeroRoutesCmsKey = (tripId: string) => ['heroRoutesCms', tripId] as const;
export const makePlaceImagesCmsKey = (tripId: string, locale: string) => ['placeImagesCms', tripId, locale] as const;
export const makeLegContentCmsKey = (tripId: string, locale: string) => ['legContentCms', tripId, locale] as const;
export const makePlaceNamesCmsKey = (tripId: string, locale: string) => ['placeNamesCms', tripId, locale] as const;
export const HydratedOverview = t0 => {
  const $ = _c(10);
  const {
    tripId,
    locale,
    overviewData,
    placeNames,
    children
  } = t0;
  let T0;
  let t1;
  if ($[0] !== locale || $[1] !== overviewData || $[2] !== placeNames || $[3] !== tripId) {
    const queryClient = new QueryClient();
    queryClient.setQueryData(makeOverviewQueryKey(tripId), overviewData);
    queryClient.setQueryData(makePlaceNamesCmsKey(tripId, locale), placeNames);
    T0 = Hydrate;
    t1 = dehydrate(queryClient);
    $[0] = locale;
    $[1] = overviewData;
    $[2] = placeNames;
    $[3] = tripId;
    $[4] = T0;
    $[5] = t1;
  } else {
    T0 = $[4];
    t1 = $[5];
  }
  let t2;
  if ($[6] !== T0 || $[7] !== children || $[8] !== t1) {
    t2 = <T0 state={t1}>{children}</T0>;
    $[6] = T0;
    $[7] = children;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
type HeroRoutesProps = {
  tripId: string;
  locale: string;
  heroRoutesCms: Record<string, HeroRouteRecord>;
  placeImagesCms?: Record<string, PlaceImageRecord>;
  legContentCms?: Record<string, LegContentRecord>;
  children: ReactNode;
};
export const HydratedHeroRoutes = t0 => {
  const $ = _c(11);
  const {
    tripId,
    locale,
    heroRoutesCms,
    placeImagesCms,
    legContentCms,
    children
  } = t0;
  let T0;
  let t1;
  if ($[0] !== heroRoutesCms || $[1] !== legContentCms || $[2] !== locale || $[3] !== placeImagesCms || $[4] !== tripId) {
    const queryClient = new QueryClient();
    queryClient.setQueryData(makeHeroRoutesCmsKey(tripId), heroRoutesCms);
    if (placeImagesCms) {
      queryClient.setQueryData(makePlaceImagesCmsKey(tripId, locale), placeImagesCms);
    }
    if (legContentCms) {
      queryClient.setQueryData(makeLegContentCmsKey(tripId, locale), legContentCms);
    }
    T0 = Hydrate;
    t1 = dehydrate(queryClient);
    $[0] = heroRoutesCms;
    $[1] = legContentCms;
    $[2] = locale;
    $[3] = placeImagesCms;
    $[4] = tripId;
    $[5] = T0;
    $[6] = t1;
  } else {
    T0 = $[5];
    t1 = $[6];
  }
  let t2;
  if ($[7] !== T0 || $[8] !== children || $[9] !== t1) {
    t2 = <T0 state={t1}>{children}</T0>;
    $[7] = T0;
    $[8] = children;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};